<template>
  <Dialog :visible="visible" @update:visible="closeDialog" modal>
    <template #header>
      <div class="text-xl">
        {{
          $t('features.tickets.create_ticket_for', { plant: plant.getName() })
        }}
      </div>
    </template>

    <div style="width: 50rem">
      <div v-if="showOpenTickets" class="mb-2">
        <div class="text-muted mb-1">
          {{ $t('features.tickets.open_tickets') }}
        </div>

        <DataTable
          :value="tickets"
          :loading="loadingTickets"
          @row-select="onRowClicked"
          size="small"
          show-gridlines
          selection-mode="single"
        >
          <template #empty>
            {{ $t('features.tickets.no_open') }}
          </template>

          <Column :header="$t('global.id')" field="number" />
          <Column :header="$t('global.title')" field="title" />
          <Column :header="$t('global.created_at')">
            <template #body="slotProps">
              <span
                v-tooltip.top="
                  formatAsDateTime(new Date(slotProps.data.created_at))
                "
              >
                {{ formatAsDate(new Date(slotProps.data.created_at)) }}
              </span>
            </template>
          </Column>
        </DataTable>
        <Divider />
      </div>

      <div class="text-muted">{{ $t('global.title') }}</div>
      <InputText
        id="title"
        v-model.trim="form.title"
        :disabled="creating"
        class="w-full my-1"
      />

      <Editor
        v-model="form.description"
        editorStyle="height: 15rem; font-size: 1rem"
        :readonly="creating"
      >
        <template #toolbar>
          <span class="ql-formats">
            <button class="ql-bold"></button>
            <button class="ql-italic"></button>
            <button class="ql-underline"></button>
            <button class="ql-strike"></button>
            <select class="ql-color"></select>
            <select class="ql-background"></select>
          </span>
        </template>
      </Editor>

      <div class="text-muted mt-2 mb-1">
        {{ $t('features.tickets.attach_child_company') }}
      </div>

      <plant-companies-multi-select
        :company-id="companyId"
        :plant="plant"
        @selection-changed="handleCompanyAttached($event)"
      />

      <div class="text-muted my-1">
        {{ $t('features.tickets.assign_users') }}
      </div>

      <MultiSelect
        v-model="assignees"
        :options="users"
        option-group-label="companyName"
        option-group-children="data"
        option-label="first_name"
        option-value="id"
        :max-selected-labels="6"
        display="chip"
        :show-toggle-all="false"
        scroll-height="25rem"
        :placeholder="$t('global.select_an_option')"
        class="w-full"
      >
        <template #option="slotProps">
          <strong v-if="slotProps.option.email === supportEmail">
            {{ slotProps.option.first_name }} {{ slotProps.option.last_name }}
          </strong>
          <div v-else>
            {{ slotProps.option.first_name }} {{ slotProps.option.last_name }}
          </div>
        </template>
      </MultiSelect>

      <small
        class="assign-to-me-trigger"
        v-if="currentUserIsEmployee && !currentUserIsAssignee"
        @click="assignToCurrentUser"
      >
        {{ $t('features.tickets.assign_me') }}
      </small>

      <div v-if="supportUserAssigned">
        <Divider />
        <span style="font-size: small">
          <strong>
            {{ $t('features.tickets.support_assigned_title') }}
            <br /><br />
            {{ $t('features.tickets.support_assigned_description') }}
            <br /><br />
            {{ $t('features.tickets.support_assigned_languages') }}
            <br />

            <i18n-t keypath="features.tickets.support_assigned_link">
              <template #link>
                <strong>
                  <a
                    href="https://support.sundata.nl/nl/articles/6309554-sundata-help-me"
                  >
                    {{ $t('features.tickets.this_support_article') }}
                  </a>
                </strong>
              </template>
            </i18n-t>
          </strong>
        </span>
      </div>
    </div>

    <template #footer>
      <Button
        @click="closeDialog"
        class="mx-2"
        severity="secondary"
        :disabled="creating"
      >
        {{ $t('global.cancel') }}
      </Button>
      <Button @click="create" :disabled="creating" :loading="creating">
        {{ $t('global.create') }}
      </Button>
    </template>
  </Dialog>
</template>

<script lang="ts">
import PlantApi from '@/data/plants/PlantApi.ts';
import TicketApi from '@/data/tickets/TicketApi.ts';
import Plant from '@/domain/model/business/Plant.ts';
import TicketCompanyUsers from '@/domain/tickets/TicketCompanyUsers.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import {
  formatAsDate,
  formatAsDateTime,
} from '@/infrastructure/support/utils/DateTimeUtils.ts';
import PlantCompaniesMultiSelect from '@/presentation/components/tickets/PlantCompaniesMultiSelect.vue';
import RouterHelper from '@/presentation/mixins/RouterHelper.js';

export default {
  name: 'CreateTicketDialog',
  components: {
    PlantCompaniesMultiSelect,
  },
  mixins: [RouterHelper, TicketCompanyUsers],
  props: {
    plant: { type: Plant, required: true },
    title: { type: String, default: null },
    description: { type: String, default: null },
    companyId: { type: Number },
    showOpenTickets: { type: Boolean, default: () => true },
    visible: { required: true },
  },
  emits: ['update:visible', 'ticket-created'],
  data() {
    return {
      loadingUsers: true,
      loadingTickets: true,
      creating: false,
      tickets: [],
      users: [],
      form: {
        title: null,
        description: null,
      },
      assignees: [],
      selectedChildCompanies: [],
      fetchFromApiFunction: null,
      supportEmail: 'support@sundata.nl',
      supportUserId: 25,
    };
  },
  watch: {
    visible() {
      if (this.visible) {
        this.form.title = this.form.title
          ? this.form.title
          : this.title
            ? this.title
            : this.plant.getName();
        this.form.description = this.form.description
          ? this.form.description
          : this.description;

        this.getUsers();

        if (this.showOpenTickets) {
          this.getOpenTickets();
        }
      }
    },
  },
  computed: {
    currentUser() {
      return useSessionStore().user;
    },
    currentUserIsEmployee() {
      return this.users.find((user) => user.id === this.currentUser.id);
    },
    currentUserIsAssignee() {
      return this.assignees.find((userId) => userId === this.currentUser.id);
    },
    supportUserAssigned() {
      return this.assignees.find((userId) => userId === this.supportUserId);
    },
    selectedChildCompaniesIds() {
      return this.selectedChildCompanies.map((company) => company.id);
    },
  },
  methods: {
    formatAsDate,
    formatAsDateTime,
    getUsers() {
      const promises = [];
      promises.push(
        this.getUsersOfCompany(
          this.companyId,
          this.plant.getOwningCompanyName(),
        ),
      );

      this.selectedChildCompaniesIds.forEach((companyId: number) => {
        const companyName = this.selectedChildCompanies.find(
          (company) => company.id === companyId,
        ).name;

        promises.push(this.getUsersOfCompany(companyId, companyName));
      });

      Promise.all(promises).then((results) => {
        this.users = this.getUniqueUsers(results);
      });
    },
    getOpenTickets() {
      this.loadingTickets = true;
      new PlantApi()
        .forwardToErrorPageOnException()
        .getSinglePlantTickets(this.companyId, this.plant.getId())
        .then((response) => {
          this.tickets = response.data.filter(
            (ticket) => ticket.closed_on === null,
          );
        })
        .finally(() => (this.loadingTickets = false));
    },
    handleCompanyAttached(event: Array<number>) {
      this.selectedChildCompanies = event;

      this.getUsers();
    },
    assignToCurrentUser() {
      const userAlreadyAssigned = this.assignees.find(
        (user) => user.id === this.currentUser.id,
      );

      if (!userAlreadyAssigned) {
        const userToAdd = this.users.find(
          (user) => user.id === this.currentUser.id,
        );
        if (userToAdd) {
          this.assignees.push(userToAdd.id);
        }
      }
    },
    create() {
      this.creating = true;
      this.loadingTickets = true;

      new TicketApi()
        .createTicket(this.companyId, this.plant.getId(), this.form)
        .then((response) => {
          this.attachChildCompaniesToTicket(response.data.id);
          this.assignUsersToTickets(response.data.id);

          this.clearForm();
          this.$emit('ticket-created');
        })
        .finally(() => {
          this.getOpenTickets();
          this.creating = false;
          this.closeDialog();
        });
    },
    assignUsersToTickets(ticketId: number) {
      const ticketApi = new TicketApi();
      this.assignees.forEach((assigneeId: number) => {
        ticketApi.assignUserToTicket(
          this.companyId,
          this.plant.getId(),
          ticketId,
          assigneeId,
        );
      });
    },
    attachChildCompaniesToTicket(ticketId: number) {
      const ticketApi = new TicketApi();
      this.selectedChildCompaniesIds.forEach((companyId: number) => {
        ticketApi.attachChildCompany(
          this.companyId,
          this.plant.getId(),
          ticketId,
          companyId,
        );
      });
    },
    clearForm() {
      this.form.title = null;
      this.form.description = null;
      this.assignees = [];
    },
    onRowClicked(event) {
      const routeDescription = {
        name: 'SingleTicket',
        params: {
          ticketId: parseInt(event.data.id),
          companyId: this.companyId,
        },
      };
      this.resolveAndFollowWMouseEvent(
        this.$router,
        routeDescription,
        event.originalEvent,
      );
    },
    closeDialog() {
      this.$emit('update:visible');
    },
  },
};
</script>

<style>
.assign-to-me-trigger {
  font-weight: bold;
  color: #ffab05;
  cursor: pointer;
}

.assign-to-me-trigger:hover {
  text-decoration: underline;
}
</style>
